import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_PartitionRRow,
    WB_PartitionRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbPartition} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Customer,
    esWB_Partition,
    esWB_PartitionR,
    esWB_Period,
    esWB_Product_Group,
    esWB_Source
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PartitionBOValidations} from "./PartitionBO.Validations";
import {BehaviorSubject} from "rxjs";
import {ASync} from "../PA.Core/Basic/ASync";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";

export interface IPartitionChangedEvent {
    PartitionBO: PartitionBO,
    Row: WB_PartitionRow,
    PropertyChanged: string,
    PropertyValue: any
}


export class PartitionBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    _PartitionSetsChanged: BehaviorSubject<any>;

    _PartitionHierarchyChanged: BehaviorSubject<any>;

    dbContext: PAdbContext;
    drWB_Partition: WB_PartitionRow;

    _PartitionPropertyChanged: BehaviorSubject<IPartitionChangedEvent>


    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Partition'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this._PartitionSetsChanged = new BehaviorSubject<any>(this)
        this._PartitionHierarchyChanged = new BehaviorSubject<any>(this)

        this.drWB_Partition = null;

        this._PartitionPropertyChanged = new BehaviorSubject<IPartitionChangedEvent>(undefined);

    }

    get WB_Partition(): WB_PartitionRow {
        return this.drWB_Partition;
    }

    get PartitionPropertyChanged(): BehaviorSubject<IPartitionChangedEvent> {
        return this._PartitionPropertyChanged
    }

    private onMainRecordChange(record: WB_PartitionRow, propertyName: string, propertyValue: any, options: PartitionBO) {
        this.PartitionPropertyChanged.next({
            PartitionBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Partition");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Partition.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Partition)
                .then((newPartitionRow: WB_PartitionRow) => {
                    newPartitionRow.setDefaults()
                    self.drWB_Partition = newPartitionRow;
                    self.drWB_Partition.cif.WB_MDSET_ID = self.LoadOptions.wbMasterDataSetId || 0
                    self.drWB_Partition.cif.WB_SALESORG_ID = self.LoadOptions.wbSalesOrgId || 0
                    self.drWB_Partition.cif.WB_SALESAREA_ID = self.LoadOptions.wbSalesAreaId || 0
                    self.drWB_Partition.cif.PARTITION_NAME=self.LoadOptions.sName||"New Partition " + self.drWB_Partition.cif.PARTITION_ID
                    self.dbContext.add(esWB_Partition, newPartitionRow)
                    self.Identity.setAll(self.WB_Partition.cif.WB_PARTITION_ID, self.WB_Partition.cif.PARTITION_ID, self.WB_Partition.cif.PARTITION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Partition, 'WB_PARTITION_ID', 'PARTITION_ID', 'PARTITION_NAME')
                    self.drWB_Partition.setupOnChangeEvents([], self.onMainRecordChange.bind(self), self)
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        var self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        if (!this.bIsLoaded)
            this.Identity.setAll(anId, anId, 'Loading Partition (' + anId + ')')

        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                var sError = "Partition already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Partition.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = PartitionBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = PartitionBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aPartitionRow: any = recordResolver.get(esWB_Partition, anId)

                    if (_.isEmpty(aPartitionRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_Partition = aPartitionRow;
                    self.Identity.setAll(self.WB_Partition.cif.WB_PARTITION_ID, self.WB_Partition.cif.PARTITION_ID, self.WB_Partition.cif.PARTITION_NAME);
                    self.Identity.listenToRecordUpdates(self.WB_Partition, 'WB_PARTITION_ID', 'PARTITION_ID', 'PARTITION_NAME')
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.drWB_Partition.setupOnChangeEvents([], self.onMainRecordChange.bind(self), self)
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();

                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Partition.cif.PARTITION_NAME + "(" + self.WB_Partition.cif.PARTITION_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                var sError = "Unable to determine dbContext for to Validate Partition.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            PartitionBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbPartition = <IwbPartition>self.drWB_Partition;
        this.SaveProgressProvider.start("Saving " + wbPartition.PARTITION_NAME + "(" + wbPartition.PARTITION_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (this.dbContext == null) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Partition.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    } else {
                        return saveFunction(esWB_Partition, self.drWB_Partition, {})
                    }
                })

            saveFunction(esWB_Partition, self.drWB_Partition, {})
                .then((ignored) => {
                    var tasks: Promise<any>[] = []
                    tasks.push(saveFunction(esWB_PartitionR, self.drWB_Partition.WB_PartitionRRows))
                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self)
                })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbPartition = <IwbPartition>self.drWB_Partition;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbPartition.PARTITION_NAME + "(" + wbPartition.WB_PARTITION_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Partition

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Partition.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    get PartitionSetsChanged(): BehaviorSubject<any> {
        return this._PartitionSetsChanged
    }

    get PartitionHierarchyChanged(): BehaviorSubject<any> {
        return this._PartitionHierarchyChanged
    }

    //</editor-fold>

    //<editor-fold desc = "Partition set operations">

    addPartitionSet(aWBCustId: number,
                    aWBPrdGrpId: number,
                    aWBPeriodId: number,
                    aWBSourceId: number): Promise<IBusinessObject> {

        const self = this
        let aPartitionRow: WB_PartitionRRow


        return new Promise<IBusinessObject>((resolve, error) => {

            self.dbContext.createRecordWithKey(esWB_PartitionR)
                .then((aNewRow: WB_PartitionRRow) => {
                    if (_.isEmpty(aNewRow)) {
                        error("Unable to create new Row")
                        return
                    }
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_PARTITION_ID = self.drWB_Partition.cif.WB_PARTITION_ID
                    aNewRow.cif.WB_CUST_ID = aWBCustId
                    aNewRow.cif.WB_PRDGRP_ID = aWBPrdGrpId
                    aNewRow.cif.WB_SOURCE_ID = aWBSourceId
                    aNewRow.cif.WB_PERIOD_ID = aWBPeriodId

                    aPartitionRow = aNewRow

                    return self.dbContext.add(esWB_PartitionR, aNewRow)

                }).then(() => {
                return aPartitionRow.loadRelations([esWB_Customer, esWB_Period, esWB_Product_Group, esWB_Source])
            }).then(() => {
                self.PartitionSetsChanged.next(self)
                resolve(self)
            }).catch((err) => {
                error(err)
            })
        })
    }

    removePartitionSet(aWB_PARTITIONR_ID: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_Partition.WB_PartitionRRows, (aPartitionRRow: WB_PartitionRRow) => {
                if (aPartitionRRow.isActive && aPartitionRRow.cif.WB_PARTITIONR_ID == aWB_PARTITIONR_ID) {
                    aPartitionRRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolverProvider   <<STATIC>> operations">


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts)
            dbContext = opts.dbContext;
        else {
            dbContext = new PAdbContext({});
        }
        const partition_options: any = {
            standardConditions: {
                [esWB_Partition]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_PartitionR]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctExcludeHasManyNamedRelations: {},
            dctIncludeBelongsToNamedRelations: {
                // This should match LoadNew loadRelations
                [esWB_PartitionR]: [esWB_Customer, esWB_Product_Group, esWB_Source, esWB_Period]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, partition_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Partition, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })

    }

    static concurrentLoadCountHint(): number {
        return 5;
    }

//</editor-fold>

};


PAEnvironment.registerBusinessObjectClass(PartitionBO, "PartitionBO", "Partition")
